import { Maybe, maybe, mt } from '@passionware/monads';
import { ensureError } from '@passionware/platform-js';
import { MetastructureField, MetastructureFieldType } from '__types__';
import { Info } from 'lucide-react';
import {
  openContactSidebar,
  closeContactSidebar,
  openModal,
  openBookingSidebar,
  openProductionSidebar
} from 'store/v1/ui/ui.actions';
import { CreateCallsheetDialog } from 'v5/features/callsheets/CreateCallsheetDialog';
import { DuplicateCallsheetDialog } from 'v5/features/callsheets/DuplicateCallsheetDialog';
import { DuplicateCallsheetFormData } from 'v5/features/callsheets/types';
import { CreateCalloutDialog } from 'v5/features/callouts/CreateCalloutDialog';
import { EditCalloutDialog } from 'v5/features/callouts/EditCalloutDialog';
import { DuplicateCalloutDialog } from 'v5/features/callouts/DuplicateCalloutDialog';
import {
  CalloutCreateFormData,
  CalloutDuplicateFormData
} from 'v5/features/callouts/callout-create.types';
import { getCalloutPublicUrl } from 'v5/api/callouts/callout-slug.logic';
import { getCalloutDetailPath } from 'v5/core/app-scope/entityWorkspaceRouting';
import { Callout } from '__types__/v1/models/Callout';
import { CustomFieldAddDialogWidget } from 'v5/features/custom-fields/config-dialog/CustomFieldAddDialogWidget';
import { myRouting } from 'v5/features/core/routing';
import { WithServices } from 'v5/platform/ts/services';
import { WithMetastructureMappingService } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';
import { WithProductionTypeService } from 'v5/services/internal/ProductionTypeService/ProductionTypeService';
import { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import { WithGroupsService } from 'v5/services/io/GroupsService/GroupsService';
import { WithProductionService } from 'v5/services/io/ProductionService/ProductionService';
import { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';
import { WithTagService } from 'v5/services/io/TagsService/TagsService';
import store from '../../../../store';
import { getCustomFieldDefinitions } from 'store/v1/custom_fields/custom_fields.selectors';
import { CORE_STRUCTURES } from '../../../../v1/helpers/coreStructures';
import { friendlyStringFromConst } from 'v1/helpers/misc';
import type {
  CreateCustomFieldScope,
  ScopedToCoreStructureRequest
} from 'v5/services/internal/MutationService/MutationService';
import type {
  AssignCustomFieldsDialogRequest,
  UserFlowService
} from './UserFlowService';
import { WithCallsheetService } from 'v5/services/io/CallsheetService/CallsheetService';
import { WithCallsheetMutationService } from 'v5/services/io/CallsheetMutationService/CallsheetMutationService';
import { WithCalloutMutationService } from 'v5/services/io/CalloutMutationService/CalloutMutationService';
import { WithCalloutsService } from 'v5/services/io/CalloutsService/CalloutsService';
import { WithNavigationService } from 'v5/services/internal/NavigationService/NavigationService';
import { WithUserPreferencesService } from 'v5/services/front/UserPreferencesService/UserPreferencesService';
import { WithToastService } from 'v5/services/front/ToastService/ToastService';
import { WithMutationService } from 'v5/services/internal/MutationService/MutationService';
import { WithMessageService } from 'v5/services/internal/MessageService/MessageService';
import { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import { WithEnterpriseMutationService } from 'v5/services/io/EnterpriseMutationService/EnterpriseMutationService';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { WithResourceMutationService } from 'v5/services/io/ResourceMutationService/ResourceMutationService';
import { WithProductionMutationService } from 'v5/services/io/ProductionMutationService/ProductionMutationService';
import { WithUploadService } from 'v5/services/io/UploadService/UploadService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { CreateEnterpriseWorkspaceDialog } from 'v5/features/core/enterprise/management/CreateEnterpriseWorkspaceDialog';
import { EditEnterpriseWorkspaceDialog } from 'v5/features/core/enterprise/management/EditEnterpriseWorkspaceDialog';
import { CreateEnterpriseTeamDialog } from 'v5/features/core/enterprise/management/CreateEnterpriseTeamDialog';
import { EditEnterpriseTeamDialog } from 'v5/features/core/enterprise/management/EditEnterpriseTeamDialog';
import { TransferCalloutDialog } from 'v5/features/callouts/TransferCalloutDialog';
import { TransferResourceDialog } from 'v5/features/resources/TransferResourceDialog';
import { TransferProductionDialog } from 'v5/features/productions/TransferProductionDialog';
import { getProductionDetailPath } from 'v5/core/app-scope/entityWorkspaceRouting';
import { internalInstance } from 'v5/i18n.internal';
import { appRouting } from 'v5/core/routing/appRouting';
import { DeleteConfirmationDialog } from 'v5/patterns/forms/dialogs/DeleteConfirmationDialog';

function isMetastructureCustomField(
  field: MetastructureField
): field is Extract<
  MetastructureField,
  { type: MetastructureFieldType.Custom }
> {
  return field.type === MetastructureFieldType.Custom;
}

function getOrderedAssignedCustomFieldIds(
  fields: MetastructureField[]
): number[] {
  // Soft-archived (`active: false`) fields stay on the metastructure but are
  // not shown as currently assigned in the add/assign dialog.
  return fields
    .filter(
      (
        field
      ): field is Extract<
        MetastructureField,
        { type: MetastructureFieldType.Custom }
      > => isMetastructureCustomField(field) && field.active
    )
    .map(field => field.custom_field_definition_id);
}

function getEnterpriseWorkspaceSlugFromPathname(): string | undefined {
  const match = window.location.pathname.match(/\/w\/([^/]+)/);
  return match?.[1];
}

function createUserFlowServiceInternals(
  config: Parameters<typeof createUserFlowService>[0]
) {
  async function resolveEnterpriseWorkspaceIdForCallout(
    explicit?: number
  ): Promise<number | undefined> {
    if (explicit != null) {
      return explicit;
    }
    const isEnterprise =
      await config.services.featureService.ensureFeature('enterprise');
    if (!isEnterprise) {
      return undefined;
    }
    const workspaceSlug = getEnterpriseWorkspaceSlugFromPathname();
    if (!workspaceSlug) {
      return undefined;
    }
    const organizationId = (store.getState().auth as { app_id: string }).app_id;
    const workspaces =
      await config.services.enterpriseService.ensureWorkspaces(organizationId);
    return workspaces.find(workspace => workspace.slug === workspaceSlug)?.id;
  }

  async function getNavigationWorkspaceSlug(
    organizationId: string,
    enterpriseWorkspaceId?: number
  ): Promise<string> {
    if (enterpriseWorkspaceId != null) {
      const workspaces =
        await config.services.enterpriseService.ensureWorkspaces(
          organizationId
        );
      const workspace = workspaces.find(
        item => item.id === enterpriseWorkspaceId
      );
      if (workspace) {
        return workspace.slug;
      }
    }
    return (store.getState().auth as { app_id: string }).app_id;
  }

  async function navigateToCalloutDetail(
    calloutId: number,
    enterpriseWorkspaceId?: number
  ) {
    const organizationId = (store.getState().auth as { app_id: string }).app_id;
    const resolvedWorkspaceId = await resolveEnterpriseWorkspaceIdForCallout(
      enterpriseWorkspaceId
    );
    const workspaces =
      await config.services.enterpriseService.ensureWorkspaces(organizationId);

    config.services.navigationService.navigate(
      getCalloutDetailPath(
        organizationId,
        {
          id: calloutId,
          enterprise_workspace_id: resolvedWorkspaceId ?? null
        },
        workspaces
      )
    );
  }

  return {
    resolveEnterpriseWorkspaceIdForCallout,
    getNavigationWorkspaceSlug,
    navigateToCalloutDetail
  };
}

export function createUserFlowService(
  config: WithServices<
    [
      WithProductionService,
      WithProductionTypeService,
      WithMetastructureMappingService,
      WithResourceTypeService,
      WithResourceService,
      WithTagService,
      WithGroupsService,
      WithCallsheetService,
      WithCallsheetMutationService,
      WithCalloutMutationService,
      WithCalloutsService,
      WithUserPreferencesService,
      WithToastService,
      WithMutationService,
      WithMessageService,
      WithFeatureService,
      WithEnterpriseMutationService,
      WithUploadService,
      WithResourceMutationService,
      WithProductionMutationService,
      WithEnterpriseService,
      WithNavigationService<Record<string, object>>,
      WithAppScopeService
    ]
  >
): UserFlowService {
  const { resolveEnterpriseWorkspaceIdForCallout, navigateToCalloutDetail } =
    createUserFlowServiceInternals(config);

  const confirmArchive = (options: {
    title: string;
    description: string;
    onConfirm: () => Promise<void>;
    successMessage: string;
  }) =>
    new Promise<void>(resolve => {
      config.services.messageService.mountElement.sendRequest({
        handler: mountProps => (
          <DeleteConfirmationDialog
            {...mountProps}
            title={options.title}
            description={options.description}
            onResolve={async confirmed => {
              if (confirmed) {
                try {
                  await options.onConfirm();
                  config.services.toastService.open({
                    type: 'success',
                    message: options.successMessage
                  });
                } catch (e) {
                  config.services.toastService.open({
                    type: 'error',
                    message: ensureError(e).message
                  });
                }
              }
              resolve();
            }}
          />
        )
      });
    });

  const getTargetInfo = (request: CreateCustomFieldScope) => {
    if (!request) {
      return null;
    }

    const coreStructureName = CORE_STRUCTURES[request.coreStructure]?.name;
    if (!coreStructureName) {
      throw new Error(`Unknown core structure: ${request.coreStructure}`);
    }
    const entityName =
      store.getState()[request.coreStructure]?.data[request.coreStructureTypeId]
        ?.name ?? '???';

    return {
      coreStructure: coreStructureName,
      coreStructureType: entityName
    };
  };

  const getTargetPrompt = (request: CreateCustomFieldScope) => {
    const targetInfo = getTargetInfo(request);
    return targetInfo ? (
      <>
        <Info className="size-5 mr-2 inline-block" />
        <span>This will be added to </span>
        <strong>
          {targetInfo.coreStructure}: {targetInfo.coreStructureType}
        </strong>
      </>
    ) : null;
  };

  const buildCustomFieldsDialogData = (
    request: ScopedToCoreStructureRequest
  ) => {
    const state = store.getState();
    const definitions = getCustomFieldDefinitions(state);
    const allCustomFields = definitions?.filter(field => !field.archived) ?? [];
    const structureType =
      state[request.coreStructure]?.data?.[request.coreStructureTypeId];
    const structureFields = (structureType?.metastructure?.fields ??
      []) as MetastructureField[];
    const initialAssignedOrder =
      getOrderedAssignedCustomFieldIds(structureFields);
    const catalog = allCustomFields.map(field => ({
      id: field.id,
      name: field.name,
      typeLabel: friendlyStringFromConst(field.data_type),
      // Timestamped.created_at is typed as Date; Redux keeps raw API JSON (ISO strings).
      created_at: field.created_at as unknown as string
    }));

    return {
      catalog,
      initialAssignedOrder
    };
  };

  const saveAssignedCustomFields = async (
    request: ScopedToCoreStructureRequest,
    assignedIds: number[]
  ) => {
    const structureType =
      store.getState()[request.coreStructure]?.data?.[
        request.coreStructureTypeId
      ];
    if (!structureType) {
      throw new Error(
        'Could not save field assignment: structure type is missing from application state.'
      );
    }

    const assignedSet = new Set(assignedIds);
    const existingFields = (structureType.metastructure?.fields ??
      []) as MetastructureField[];
    const existingCustomIds = new Set<number>();
    const nextFields: MetastructureField[] = existingFields.map(field => {
      if (field.type !== MetastructureFieldType.Custom) {
        return field;
      }
      const id = field.custom_field_definition_id;
      existingCustomIds.add(id);
      // Unassigned custom fields are soft-archived, never hard-removed.
      return { ...field, active: assignedSet.has(id) };
    });

    for (const id of assignedSet) {
      if (!existingCustomIds.has(id)) {
        nextFields.push({
          active: true,
          custom_field_definition_id: id,
          default: '',
          locked: false,
          required: false,
          type: MetastructureFieldType.Custom
        });
      }
    }

    const updateAction = CORE_STRUCTURES[request.coreStructure]?.updateAction;
    if (!updateAction) {
      throw new Error(
        'Could not save field assignment: this structure has no update action configured.'
      );
    }

    const result = await store.dispatch(
      updateAction(request.coreStructureTypeId, {
        ...structureType,
        metastructure: {
          ...structureType.metastructure,
          fields: nextFields
        }
      })
    );
    if (result.error) {
      throw ensureError(result.error);
    }
  };

  const openAssignCustomFieldsDialogImpl = async (
    request: AssignCustomFieldsDialogRequest
  ) => {
    try {
      const features =
        await config.services.featureService.ensureFeatureFlags();
      config.services.messageService.mountElement.sendRequest({
        handler: mountProps => (
          <CustomFieldAddDialogWidget
            {...mountProps}
            features={features}
            prompt={request.prompt}
            catalog={request.catalog}
            initialAssignedOrder={request.initialAssignedOrder}
            onCreate={async field => {
              try {
                if (request.onCreate) {
                  await request.onCreate(field);
                } else {
                  await config.services.mutationService.createCustomField(
                    field,
                    undefined
                  );
                }
                config.services.toastService.open({
                  type: 'success',
                  message: 'Created custom field successfully'
                });
              } catch (e) {
                config.services.toastService.open({
                  type: 'error',
                  message: ensureError(e).message
                });
                throw e;
              }
            }}
            onSaveFields={async assignedIds => {
              try {
                await request.onSaveFields(assignedIds);
                config.services.toastService.open({
                  type: 'success',
                  message: 'Updated fields successfully'
                });
              } catch (e) {
                config.services.toastService.open({
                  type: 'error',
                  message: ensureError(e).message
                });
                throw e;
              }
            }}
          />
        )
      });
    } catch (e) {
      config.services.toastService.open({
        type: 'error',
        message: ensureError(e).message
      });
    }
  };

  // Shared callout creation logic
  const createCalloutFlow = async (
    data: CalloutCreateFormData,
    enterpriseWorkspaceId?: number
  ) => {
    try {
      const resolvedWorkspaceId = await resolveEnterpriseWorkspaceIdForCallout(
        enterpriseWorkspaceId
      );
      const result = await config.services.calloutMutationService.createCallout(
        {
          title: data.title,
          slug: data.slug,
          resource_type_id: data.resource_type_id,
          mappings: data.mappings,
          metadata: { useNewForm: true },
          ...(resolvedWorkspaceId != null
            ? { enterprise_workspace_id: resolvedWorkspaceId }
            : {})
        }
      );
      config.services.toastService.open({
        type: 'success',
        message: 'Callout created successfully'
      });
      return result.id;
    } catch (e) {
      config.services.toastService.open({
        type: 'error',
        message: ensureError(e).message
      });
      return undefined;
    }
  };

  const duplicateCalloutFlow = async (
    sourceCallout: Callout,
    data: CalloutDuplicateFormData
  ) => {
    try {
      const enterpriseWorkspaceId =
        sourceCallout.enterprise_workspace_id ?? undefined;
      const result =
        await config.services.calloutMutationService.duplicateCallout({
          sourceCallout,
          ...data,
          ...(enterpriseWorkspaceId != null
            ? { enterprise_workspace_id: enterpriseWorkspaceId }
            : {})
        });
      config.services.toastService.open({
        type: 'success',
        message: 'Callout duplicated successfully'
      });
      return result.id;
    } catch (e) {
      config.services.toastService.open({
        type: 'error',
        message: ensureError(e).message
      });
      return undefined;
    }
  };

  // Shared callsheet creation logic
  const createCallsheetFlow = async (
    workspace: string,
    request: Parameters<UserFlowService['createCallsheet']>[1]
  ) => {
    try {
      const productions = await Promise.all(
        request.selectedProductionIds.map(id =>
          config.services.productionService.ensureProduction(id)
        )
      );

      // Generate callsheet data based on mode (single date vs range)
      const dateSelection = request.dateSelection;
      const generatedData =
        dateSelection?.type === 'range'
          ? await config.services.callsheetService.generateCallsheetDataFromProductionsForRange(
              productions,
              {
                dateRange: { from: dateSelection.from, to: dateSelection.to },
                callTime: request.generalCallTime
              }
            )
          : await config.services.callsheetService.generateCallsheetDataFromProductions(
              productions,
              {
                selectedDate: dateSelection?.date ?? request.date!,
                callTime: request.generalCallTime
              }
            );

      const result =
        await config.services.callsheetMutationService.createCallsheet(
          workspace,
          {
            ...request,
            generatedData: generatedData.data
          }
        );
      config.services.toastService.open({
        type: 'success',
        message: 'Callsheet created successfully'
      });
      return result.id;
    } catch (e) {
      config.services.toastService.open({
        type: 'error',
        message: ensureError(e).message
      });
    }
  };

  return {
    createCustomField: async request => {
      const channel =
        await config.services.messageService.createCustomField.sendRequest({
          type: 'create',
          prompt: getTargetPrompt(request)
        });

      let result: Maybe<{ id: number }> = maybe.ofAbsent();

      for await (const payload of channel) {
        try {
          result = await config.services.mutationService.createCustomField(
            payload,
            request
          );
          config.services.toastService.open({
            type: 'success',
            message: 'Created custom field successfully'
          });
          channel.emit(mt.ofSuccess(void 0, void 0));
        } catch (e) {
          channel.emit(mt.ofError(void 0, ensureError(e)));
          config.services.toastService.open({
            type: 'error',
            message: ensureError(e).message
          });
        }
      }

      return maybe.mapOrElse(
        result,
        result => ({ action: 'created', id: result.id }),
        { action: 'canceled' }
      );
    },
    editCustomField: async (id, payload) => {
      const channel =
        await config.services.messageService.createCustomField.sendRequest({
          type: 'edit',
          customField: payload
        });

      let created = false;

      for await (const payload of channel) {
        try {
          await config.services.mutationService.updateCustomField(id, payload);
          config.services.toastService.open({
            type: 'success',
            message: 'Updated custom field successfully'
          });
          created = true;
          channel.emit(mt.ofSuccess(void 0, void 0));
        } catch (e) {
          channel.emit(mt.ofError(void 0, ensureError(e)));
          config.services.toastService.open({
            type: 'error',
            message: ensureError(e).message
          });
        }
      }

      return created ? { action: 'updated' } : { action: 'canceled' };
    },
    openAddCustomFieldsDialog: async request => {
      try {
        const { catalog, initialAssignedOrder } =
          buildCustomFieldsDialogData(request);
        await openAssignCustomFieldsDialogImpl({
          prompt: getTargetPrompt(request),
          catalog,
          initialAssignedOrder,
          onCreate: async field => {
            await config.services.mutationService.createCustomField(
              field,
              request
            );
          },
          onSaveFields: async assignedIds => {
            await saveAssignedCustomFields(request, assignedIds);
          }
        });
      } catch (e) {
        config.services.toastService.open({
          type: 'error',
          message: ensureError(e).message
        });
      }
    },
    openAssignCustomFieldsDialog: request => {
      void openAssignCustomFieldsDialogImpl(request);
    },
    openCreateCallsheetDialog: (workspace, options) => {
      config.services.messageService.mountElement.sendRequest({
        handler: mountProps => (
          <CreateCallsheetDialog
            {...mountProps}
            {...options}
            onSubmit={async data => {
              const id = await createCallsheetFlow(workspace, data);
              mountProps.close();
              if (maybe.isPresent(id)) {
                config.services.navigationService.navigate(
                  myRouting.forWorkspace(workspace).callsheets().detail(id)
                );
              }
            }}
            services={config.services}
          />
        )
      });
    },
    createCallsheet: createCallsheetFlow,
    openDuplicateCallsheetDialog: async (workspace, callsheetId) => {
      try {
        // Fetch the original callsheet
        const originalCallsheet =
          await config.services.callsheetService.ensureCallsheet(callsheetId);

        config.services.messageService.mountElement.sendRequest({
          handler: mountProps => (
            <DuplicateCallsheetDialog
              {...mountProps}
              originalCallsheet={originalCallsheet}
              onSubmit={async data => {
                const id = await duplicateCallsheetFlow(workspace, data);
                mountProps.close();
                if (maybe.isPresent(id)) {
                  config.services.navigationService.navigate(
                    myRouting.forWorkspace(workspace).callsheets().detail(id)
                  );
                }
              }}
              services={config.services}
            />
          )
        });
      } catch (e) {
        config.services.toastService.open({
          type: 'error',
          message: ensureError(e).message
        });
      }
    },
    duplicateCallsheet: duplicateCallsheetFlow,
    openResourceSidebar: (resourceId: number) => {
      // For now, create a minimal resource object with just the ID
      // The sidebar will load the full resource data
      const minimalResource = { id: resourceId };
      store.dispatch(openContactSidebar(minimalResource));
    },

    openProductionSidebar: (productionId: number) => {
      store.dispatch(openProductionSidebar({ id: productionId }));
    },

    openBookingSidebar: (bookingId: number, options = {}) => {
      const minimalBooking = { id: bookingId };
      store.dispatch(openBookingSidebar(minimalBooking, options));
    },

    openExpenseReceiptsModal: ({
      expenseId,
      productionId,
      budgetExpenseId
    }: {
      expenseId: number;
      productionId: number;
      budgetExpenseId: number;
    }) => {
      store.dispatch(
        openModal('ExpenseReceiptsModal', {
          expenseId,
          productionId,
          budgetExpenseId
        })
      );
    },

    openEditExpenseModal: ({
      expenseId,
      productionBudgetExpenseId,
      productionId,
      expense: expensePayload,
      production: productionPayload
    }) => {
      store.dispatch(
        openModal('ExpenseCreateModal', {
          expense: expensePayload
            ? {
                ...expensePayload,
                id: expenseId,
                production_budget_expense_id: productionBudgetExpenseId
              }
            : {
                id: expenseId,
                production_budget_expense_id: productionBudgetExpenseId
              },
          production: productionPayload ?? { id: productionId }
        })
      );
    },

    openCreateBookingModal: () => {
      store.dispatch(openModal('ResourceEventsModal'));
    },

    openCreateResourceModal: options => {
      store.dispatch({
        type: 'OPEN_MODAL',
        modalType: 'ResourceCreateModal',
        data:
          options?.resourceTypeId || options?.enterpriseWorkspaceId != null
            ? {
                ...(options?.resourceTypeId != null
                  ? { resource_type_id: options.resourceTypeId }
                  : {}),
                ...(options?.enterpriseWorkspaceId != null
                  ? { enterprise_workspace_id: options.enterpriseWorkspaceId }
                  : {})
              }
            : undefined
      });
    },

    openCreateShortlistModal: () => {
      // Call the v1 redux action to open the shortlist creation modal
      store.dispatch(
        openModal('ShortlistCreateModal', {
          create: true
        })
      );
    },

    shareShortlist: (publicId, workspace) => {
      if (!publicId) {
        config.services.toastService.open({
          type: 'error',
          message: 'This shortlist does not have a public link'
        });
        return;
      }

      const id = publicId.toLowerCase();
      const { origin } = window.location;

      const publicUrl = `${origin}/app/${workspace}/p/shortlists/${id}`;
      window.open(publicUrl, '_blank');
    },

    openCreateCalloutModal: async (options?: {
      enterpriseWorkspaceId?: number;
    }) => {
      config.services.messageService.mountElement.sendRequest({
        handler: mountProps => (
          <CreateCalloutDialog
            {...mountProps}
            enterpriseWorkspaceId={options?.enterpriseWorkspaceId}
            onSubmit={async data => {
              const id = await createCalloutFlow(
                data,
                options?.enterpriseWorkspaceId
              );
              mountProps.close();
              if (maybe.isPresent(id)) {
                await navigateToCalloutDetail(
                  id,
                  options?.enterpriseWorkspaceId
                );
              }
            }}
            services={config.services}
          />
        )
      });
    },

    openEditCalloutDialog: async callout => {
      config.services.messageService.mountElement.sendRequest({
        handler: mountProps => (
          <EditCalloutDialog
            {...mountProps}
            callout={callout}
            onSubmit={async data => {
              try {
                await config.services.calloutMutationService.updateCallout(
                  callout.id,
                  {
                    title: data.title,
                    resource_type_id: data.resource_type_id,
                    ...(data.slug ? { slug: data.slug } : {})
                  }
                );
                config.services.toastService.open({
                  type: 'success',
                  message: 'Callout updated successfully'
                });
                mountProps.close();
              } catch (e) {
                config.services.toastService.open({
                  type: 'error',
                  message: ensureError(e).message
                });
                throw e;
              }
            }}
            services={config.services}
          />
        )
      });
    },

    openDuplicateCalloutDialog: async callout => {
      config.services.messageService.mountElement.sendRequest({
        handler: mountProps => (
          <DuplicateCalloutDialog
            {...mountProps}
            callout={callout}
            onSubmit={async data => {
              const id = await duplicateCalloutFlow(callout, data);
              mountProps.close();
              if (maybe.isPresent(id)) {
                await navigateToCalloutDetail(
                  id,
                  callout.enterprise_workspace_id ?? undefined
                );
              }
            }}
            services={config.services}
          />
        )
      });
    },

    shareCallout: async (callout, workspace) => {
      const workspaces =
        await config.services.enterpriseService.ensureWorkspaces(workspace);

      const publicUrl = getCalloutPublicUrl(workspace, callout, {
        workspaces
      });

      if (!publicUrl) {
        config.services.toastService.open({
          type: 'error',
          message: 'This callout does not have a public link'
        });
        return;
      }

      const { origin } = window.location;
      window.open(`${origin}${publicUrl}`, '_blank');
    },

    openCreateEnterpriseWorkspaceDialog: (organizationId, options) => {
      config.services.messageService.mountElement.sendRequest({
        handler: mountProps => (
          <CreateEnterpriseWorkspaceDialog
            {...mountProps}
            services={config.services}
            onSubmit={async data => {
              try {
                const workspace =
                  await config.services.enterpriseMutationService.createWorkspace(
                    {
                      organizationId,
                      name: data.name.trim(),
                      slug: undefined,
                      iconUrl: data.iconUrl.trim() || null
                    }
                  );
                config.services.toastService.open({
                  type: 'success',
                  message: 'Workspace created'
                });
                options?.onCreated?.(workspace);
              } catch (e) {
                config.services.toastService.open({
                  type: 'error',
                  message: ensureError(e).message
                });
                throw e;
              }
            }}
          />
        )
      });
    },

    openEditEnterpriseWorkspaceDialog: (organizationId, workspace) => {
      config.services.messageService.mountElement.sendRequest({
        handler: mountProps => (
          <EditEnterpriseWorkspaceDialog
            {...mountProps}
            services={config.services}
            workspace={workspace}
            onSubmit={async data => {
              try {
                await config.services.enterpriseMutationService.updateWorkspace(
                  {
                    organizationId,
                    id: workspace.id,
                    name: data.name.trim(),
                    iconUrl: data.iconUrl.trim() || null
                  }
                );
                config.services.toastService.open({
                  type: 'success',
                  message: 'Workspace updated'
                });
              } catch (e) {
                config.services.toastService.open({
                  type: 'error',
                  message: ensureError(e).message
                });
                throw e;
              }
            }}
          />
        )
      });
    },

    openCreateEnterpriseTeamDialog: ({
      organizationId,
      workspaceId,
      onCreated
    }) => {
      config.services.messageService.mountElement.sendRequest({
        handler: mountProps => (
          <CreateEnterpriseTeamDialog
            {...mountProps}
            services={config.services}
            onSubmit={async data => {
              try {
                const team =
                  await config.services.enterpriseMutationService.createTeam({
                    organizationId,
                    workspaceId,
                    name: data.name.trim(),
                    slug: undefined,
                    iconUrl: data.iconUrl.trim() || null,
                    iconColor: data.iconColor.trim() || null
                  });
                config.services.toastService.open({
                  type: 'success',
                  message: 'Team created'
                });
                onCreated?.(team);
              } catch (e) {
                config.services.toastService.open({
                  type: 'error',
                  message: ensureError(e).message
                });
                throw e;
              }
            }}
          />
        )
      });
    },

    openEditEnterpriseTeamDialog: (organizationId, team) => {
      config.services.messageService.mountElement.sendRequest({
        handler: mountProps => (
          <EditEnterpriseTeamDialog
            {...mountProps}
            services={config.services}
            team={team}
            onSubmit={async data => {
              try {
                await config.services.enterpriseMutationService.updateTeam({
                  organizationId,
                  id: team.id,
                  name: data.name.trim(),
                  iconUrl: data.iconUrl.trim() || null,
                  iconColor: data.iconColor.trim() || null
                });
                config.services.toastService.open({
                  type: 'success',
                  message: 'Team updated'
                });
              } catch (e) {
                config.services.toastService.open({
                  type: 'error',
                  message: ensureError(e).message
                });
                throw e;
              }
            }}
          />
        )
      });
    },

    archiveEnterpriseWorkspace: (organizationId, workspace) => {
      void confirmArchive({
        title: 'Archive workspace',
        description:
          'Archive this workspace? Teams and data remain but it will be hidden from navigation.',
        successMessage: 'Workspace archived',
        onConfirm: async () => {
          await config.services.enterpriseMutationService.archiveWorkspace({
            organizationId,
            id: workspace.id,
            archived: undefined
          });
        }
      });
    },

    archiveEnterpriseTeam: (organizationId, team) => {
      void confirmArchive({
        title: 'Archive team',
        description: 'Archive this team? It will be hidden from navigation.',
        successMessage: 'Team archived',
        onConfirm: async () => {
          await config.services.enterpriseMutationService.archiveTeam({
            organizationId,
            id: team.id,
            archived: undefined
          });
        }
      });
    },

    unarchiveEnterpriseWorkspace: (organizationId, workspace) => {
      void config.services.enterpriseMutationService
        .archiveWorkspace({
          organizationId,
          id: workspace.id,
          archived: false
        })
        .then(() => {
          config.services.toastService.open({
            type: 'success',
            message: 'Workspace restored'
          });
        })
        .catch(error => {
          config.services.toastService.open({
            type: 'error',
            message: ensureError(error).message
          });
        });
    },

    unarchiveEnterpriseTeam: (organizationId, team) => {
      void config.services.enterpriseMutationService
        .archiveTeam({
          organizationId,
          id: team.id,
          archived: false
        })
        .then(() => {
          config.services.toastService.open({
            type: 'success',
            message: 'Team restored'
          });
        })
        .catch(error => {
          config.services.toastService.open({
            type: 'error',
            message: ensureError(error).message
          });
        });
    },

    openTransferResourceDialog: ({
      organizationId,
      resourceIds,
      resourceName,
      currentWorkspaceId
    }) => {
      const isBatch = resourceIds.length > 1;
      config.services.messageService.mountElement.sendRequest({
        handler: mountProps => (
          <TransferResourceDialog
            {...mountProps}
            organizationId={organizationId}
            resourceName={resourceName}
            resourceCount={resourceIds.length}
            currentWorkspaceId={currentWorkspaceId}
            services={config.services}
            onSubmit={async workspace => {
              await config.services.resourceMutationService.transferResourcesToWorkspace(
                {
                  ids: resourceIds,
                  enterprise_workspace_id: workspace.id
                }
              );
              config.services.toastService.open({
                type: 'success',
                message: internalInstance.getFixedT(
                  null,
                  'resources',
                  'TransferResourceDialog'
                )('success', { count: resourceIds.length })
              });
              mountProps.close();
              store.dispatch(closeContactSidebar());
              if (isBatch) {
                config.services.navigationService.navigate(
                  appRouting
                    .org(organizationId)
                    .workspace(workspace.slug)
                    .resources.root()
                );
              } else {
                config.services.navigationService.navigate(
                  appRouting
                    .org(organizationId)
                    .workspace(workspace.slug)
                    .contacts.detail(resourceIds[0]!)
                );
              }
            }}
          />
        )
      });
    },

    openTransferCalloutDialog: ({
      organizationId,
      calloutIds,
      calloutName,
      currentWorkspaceId
    }) => {
      const isBatch = calloutIds.length > 1;
      config.services.messageService.mountElement.sendRequest({
        handler: mountProps => (
          <TransferCalloutDialog
            {...mountProps}
            organizationId={organizationId}
            calloutName={calloutName}
            calloutCount={calloutIds.length}
            currentWorkspaceId={currentWorkspaceId}
            services={config.services}
            onSubmit={async workspace => {
              await config.services.calloutMutationService.transferCalloutsToWorkspace(
                {
                  ids: calloutIds,
                  enterprise_workspace_id: workspace.id
                }
              );
              config.services.toastService.open({
                type: 'success',
                message: internalInstance.getFixedT(
                  null,
                  'callouts',
                  'TransferCalloutDialog'
                )('success', { count: calloutIds.length })
              });
              mountProps.close();
              if (isBatch) {
                config.services.navigationService.navigate(
                  appRouting
                    .org(organizationId)
                    .workspace(workspace.slug)
                    .callouts.root()
                );
              } else {
                config.services.navigationService.navigate(
                  appRouting
                    .org(organizationId)
                    .workspace(workspace.slug)
                    .callouts.detail(calloutIds[0]!)
                );
              }
            }}
          />
        )
      });
    },

    openTransferProductionDialog: ({
      organizationId,
      productionIds,
      productionName,
      currentWorkspaceId,
      currentTeamId
    }) => {
      const isBatch = productionIds.length > 1;
      config.services.messageService.mountElement.sendRequest({
        handler: mountProps => (
          <TransferProductionDialog
            {...mountProps}
            organizationId={organizationId}
            productionName={productionName}
            productionCount={productionIds.length}
            currentWorkspaceId={currentWorkspaceId}
            currentTeamId={currentTeamId}
            services={config.services}
            onSubmit={async ({ workspace, team }) => {
              await config.services.productionMutationService.transferProductionsToScope(
                {
                  ids: productionIds,
                  enterprise_workspace_id: workspace.id,
                  enterprise_team_id: team.id
                }
              );
              config.services.toastService.open({
                type: 'success',
                message: internalInstance.getFixedT(
                  null,
                  'productions',
                  'TransferProductionDialog'
                )('success', { count: productionIds.length })
              });
              mountProps.close();
              if (isBatch) {
                config.services.navigationService.navigate(
                  appRouting
                    .org(organizationId)
                    .workspace(workspace.slug)
                    .team(team.slug)
                    .productions.root()
                );
              } else {
                config.services.navigationService.navigate(
                  getProductionDetailPath(
                    organizationId,
                    {
                      id: productionIds[0]!,
                      enterprise_workspace_id: workspace.id,
                      enterprise_team_id: team.id
                    },
                    [workspace],
                    [{ id: team.id, slug: team.slug }]
                  )
                );
              }
            }}
          />
        )
      });
    }
  };

  // Duplicate callsheet flow
  async function duplicateCallsheetFlow(
    workspace: string,
    request: DuplicateCallsheetFormData
  ) {
    try {
      const result =
        await config.services.callsheetMutationService.duplicateCallsheet(
          workspace,
          request
        );

      config.services.toastService.open({
        type: 'success',
        message: 'Callsheet duplicated successfully'
      });
      return result.id;
    } catch (e) {
      config.services.toastService.open({
        type: 'error',
        message: ensureError(e).message
      });
    }
  }
}
